import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { PackageDimensions } from '../types';

export interface ShippingTier {
  id: string;
  name: string;
  description: string;
  cost: number;
  freeShippingThreshold?: number; // Minimum order amount for free shipping
  deliveryTimeMin: number; // Minimum delivery days
  deliveryTimeMax: number; // Maximum delivery days
  processingTimeMin: number; // Minimum processing days
  processingTimeMax: number; // Maximum processing days
  active: boolean;
  sortOrder: number; // For displaying in order
  maxWeight?: number; // Maximum weight in lbs
  zones?: string[]; // Geographic zones this tier applies to
}

export type RateProvider = 'easypost' | 'dhl';

export interface ShippingConfiguration {
  id: string;
  tiers: ShippingTier[];
  lastUpdated: Date;
  version: string;
  expressShippingDiscountPercent: number;
  rateProvider?: RateProvider;
}

// Cache for shipping configuration
let cachedConfig: ShippingConfiguration | null = null;
let lastFetchTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get shipping configuration from Firebase with caching
 */
export async function getShippingConfiguration(): Promise<ShippingConfiguration | null> {
  const now = Date.now();
  
  // Return cached config if it's still fresh
  if (cachedConfig && (now - lastFetchTime) < CACHE_DURATION) {
    return cachedConfig;
  }

  try {
    const configDoc = await getDoc(doc(db, 'shippingConfig', 'main'));
    
    if (configDoc.exists()) {
      const data = configDoc.data();
      const config: ShippingConfiguration = {
        id: configDoc.id,
        tiers: data.tiers || [],
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        version: data.version || '1.0.0',
        expressShippingDiscountPercent: data.expressShippingDiscountPercent || 0,
        rateProvider: (data.rateProvider as RateProvider) || 'easypost'
      };
      
      cachedConfig = config;
      lastFetchTime = now;
      return config;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching shipping configuration:', error);
    return null;
  }
}

/**
 * Get shipping tier by ID
 */
export async function getShippingTierById(tierId: string): Promise<ShippingTier | null> {
  const config = await getShippingConfiguration();
  if (!config) return null;
  
  return config.tiers.find(tier => tier.id === tierId) || null;
}

/**
 * Get shipping tier name by ID
 */
export async function getShippingTierName(tierId: string): Promise<string> {
  const tier = await getShippingTierById(tierId);
  return tier ? tier.name : formatLegacyShippingOption(tierId);
}

/**
 * Format legacy shipping option names
 */
function formatLegacyShippingOption(option: string): string {
  switch (option?.toLowerCase()) {
    case 'standard':
      return 'Standard Shipping';
    case 'express':
    case 'expedited':
      return 'Express Shipping';
    case 'priority':
      return 'Priority Shipping';
    case 'overnight':
      return 'Overnight Shipping';
    default:
      return option ? `${option.charAt(0).toUpperCase()}${option.slice(1)} Shipping` : 'Unknown Shipping';
  }
}

/**
 * Get all active shipping tiers
 */
export async function getActiveShippingTiers(): Promise<ShippingTier[]> {
  const config = await getShippingConfiguration();
  if (!config) return [];
  
  return config.tiers
    .filter(tier => tier.active)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Get express shipping discount percentage
 */
export async function getExpressShippingDiscountPercent(): Promise<number> {
  const config = await getShippingConfiguration();
  return config?.expressShippingDiscountPercent || 0;
}

/**
 * Clear the shipping configuration cache (useful for admin updates)
 */
export const clearShippingConfigCache = (): void => {
  cachedConfig = null;
  lastFetchTime = 0;
};

/**
 * DHL Express rate result interface
 */
export interface DhlExpressRateResult {
  success: boolean;
  rate: number | null;
  error?: string;
}

/**
 * Fetch DHL Express rate from backend API for admin orders
 */
export async function fetchDhlExpressRateForAdmin(
  orderItems: any[],
  shippingAddress: any,
  maxRetries: number = 2
): Promise<DhlExpressRateResult> {
  let lastError: any = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001';
      
      const response = await fetch(`${apiBaseUrl}/shipping/express-rate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cartItems: orderItems,
          shippingAddress
        })
      });

      const result = await response.json();
      
      if (result.success && result.rate) {
        // Apply express shipping discount if configured
        const discountPercent = await getExpressShippingDiscountPercent();
        const originalRate = result.rate.cost;
        const discountedRate = applyShippingDiscount(originalRate, discountPercent);
        
        return {
          success: true,
          rate: discountedRate
        };
      } else {
        lastError = result.error || 'DHL Express not available';
        
        if (attempt === maxRetries) {
          return {
            success: false,
            rate: null,
            error: lastError
          };
        }
      }
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries) {
        return {
          success: false,
          rate: null,
          error: 'Failed to get DHL Express rate'
        };
      }
    }
  }

  return {
    success: false,
    rate: null,
    error: lastError
  };
}

/**
 * Auto-select and persist package dimensions for an order
 */
export async function autoSelectBoxForOrder(orderId: string): Promise<PackageDimensions | null> {
  const rawBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001';
  const normalizedBase = rawBase.replace(/\/+$/, '').replace(/\/api$/, '');
  const url = `${normalizedBase}/api/shipping/auto-box/${orderId}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  let result: any = null;
  try {
    result = await response.json();
  } catch (err) {
    throw new Error('Unexpected response while selecting box');
  }

  if (response.ok && result?.success && result?.packageDimensions) {
    return result.packageDimensions as PackageDimensions;
  }

  throw new Error(result?.error || 'Failed to auto-select box');
}

/**
 * Apply shipping discount percentage to rate
 */
function applyShippingDiscount(originalRate: number, discountPercent: number): number {
  if (!discountPercent || discountPercent <= 0) {
    return originalRate;
  }
  
  const discountAmount = originalRate * (discountPercent / 100);
  return Math.max(0, originalRate - discountAmount);
}

/**
 * Calculate shipping cost with DHL Express rate support
 */
export async function calculateShippingCostForAdmin(
  tier: ShippingTier,
  subtotal: number,
  orderItems?: any[],
  shippingAddress?: any
): Promise<number> {
  // For express shipping, try to get DHL rate first
  if (tier.id === 'express' && orderItems && shippingAddress) {
    const dhlResult = await fetchDhlExpressRateForAdmin(orderItems, shippingAddress);
    if (dhlResult.success && dhlResult.rate !== null) {
      return dhlResult.rate;
    }
    // If DHL rate fails, fall back to Firebase config below
    console.warn('DHL Express rate failed, falling back to Firebase config:', dhlResult.error);
  }

  // Fallback to Firebase configuration
  if (tier.freeShippingThreshold && subtotal >= tier.freeShippingThreshold) {
    return 0;
  }
  
  return tier.cost;
} 
